import { useState, useEffect, useRef, useCallback } from "react";
import { useWindowManager } from "../../context/WindowManagerContext";
import { useAuth } from "../../context/AuthContext";
import { PINNED_APPS } from "../../lib/appRegistry";
import { StartMenu } from "./StartMenu";
import { PersonalizationMenu } from "./PersonalizationMenu";
import { TaskbarPreview, usePreviewHover } from "./TaskbarPreview";
import { MiniCalendar } from "./MiniCalendar";
import { useContextMenu, type ContextMenuItem } from "./ContextMenu";

interface TaskbarProps {
  provider: string;
  onOpenWindow: (id: string) => void;
  activeWindowId: string | null;
}

const STORAGE_KEY_PINNED_ORDER = "opentool-pinned-order";

function loadPinnedOrder(): string[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PINNED_ORDER);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function savePinnedOrder(order: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY_PINNED_ORDER, JSON.stringify(order));
  } catch { /* ignore */ }
}

function Clock({ onClick }: { onClick: () => void }) {
  const [time, setTime] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 30_000);
    return () => clearInterval(interval);
  }, []);

  const hours = time.getHours().toString().padStart(2, "0");
  const minutes = time.getMinutes().toString().padStart(2, "0");
  const dateStr = time.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  return (
    <button className="wm-taskbar__tray-datetime wm-taskbar__tray-datetime--clickable" onClick={onClick}>
      <span className="wm-taskbar__tray-clock">
        {hours}:{minutes}
      </span>
      <span className="wm-taskbar__tray-date">{dateStr}</span>
    </button>
  );
}

function TrayProjectWidget() {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onEnter = () => {
    timerRef.current = setTimeout(() => setVisible(true), 300);
  };
  const onLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setVisible(false);
  };

  return (
    <div
      className="wm-taskbar__tray-project-wrapper"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="wm-taskbar__tray-project">
        <span className="wm-taskbar__tray-prompt" style={{ color: '#4f8fff', fontWeight: 700 }}>&gt;</span><span style={{ color: '#e6edf3', fontWeight: 700 }}>tool</span><span style={{ color: '#4f8fff', fontWeight: 700 }}>_os</span>
      </div>
      {visible && <TrayProjectCard />}
    </div>
  );
}

function TrayProjectCard() {
  const [info, setInfo] = useState<{ git_sha?: string; image_tag?: string; environment?: string; version?: string }>({});
  useEffect(() => {
    fetch('/api/status').then(r => r.json()).then(setInfo).catch(() => {});
  }, []);
  const env = info.environment || 'local';
  const tag = info.image_tag || 'local';
  const sha = info.git_sha || __GIT_SHA__;
  const ver = info.version || '?';
  const envColor = env === 'prod' ? '#34d399' : env === 'dev' ? '#fbbf24' : env === 'lab' ? '#a78bfa' : '#484f58';

  return (
    <div className="tray-project-card">
      <svg
        className="tray-project-card__circuit"
        viewBox="0 0 200 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <line x1="20" y1="20" x2="80" y2="20" stroke="rgba(79,143,255,0.08)" strokeWidth="1" />
        <line x1="80" y1="20" x2="80" y2="60" stroke="rgba(79,143,255,0.08)" strokeWidth="1" />
        <line x1="80" y1="60" x2="140" y2="60" stroke="rgba(79,143,255,0.06)" strokeWidth="1" />
        <line x1="140" y1="60" x2="140" y2="100" stroke="rgba(79,143,255,0.06)" strokeWidth="1" />
        <line x1="40" y1="50" x2="40" y2="90" stroke="rgba(79,143,255,0.07)" strokeWidth="1" />
        <line x1="40" y1="90" x2="120" y2="90" stroke="rgba(79,143,255,0.05)" strokeWidth="1" />
        <line x1="100" y1="10" x2="180" y2="10" stroke="rgba(79,143,255,0.06)" strokeWidth="1" />
        <line x1="180" y1="10" x2="180" y2="50" stroke="rgba(79,143,255,0.07)" strokeWidth="1" />
        <line x1="60" y1="70" x2="160" y2="70" stroke="rgba(79,143,255,0.05)" strokeWidth="1" />
        <circle cx="80" cy="20" r="2" fill="rgba(79,143,255,0.12)" />
        <circle cx="80" cy="60" r="2" fill="rgba(79,143,255,0.10)" />
        <circle cx="140" cy="60" r="2" fill="rgba(79,143,255,0.10)" />
        <circle cx="40" cy="50" r="2" fill="rgba(79,143,255,0.12)" />
        <circle cx="40" cy="90" r="2" fill="rgba(79,143,255,0.10)" />
        <circle cx="180" cy="10" r="2" fill="rgba(79,143,255,0.10)" />
        <circle cx="180" cy="50" r="2" fill="rgba(79,143,255,0.12)" />
        <rect x="118" y="88" width="4" height="4" fill="rgba(79,143,255,0.08)" />
        <rect x="138" y="58" width="4" height="4" fill="rgba(79,143,255,0.08)" />
      </svg>

      <div className="tray-project-card__row">
        <span className="tray-project-card__prompt">&gt;</span>
        <span className="tray-project-card__name">tool_os</span>
        <span style={{ fontSize: '0.7em', color: '#484f58', marginLeft: 6 }}>v{ver}</span>
      </div>
      <div className="tray-project-card__meta">
        <span style={{ color: '#4f8fff', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85em' }}>{sha}</span>
        <span className="tray-project-card__sep">/</span>
        <span style={{ color: '#8b949e', fontFamily: 'monospace', fontSize: '0.85em' }}>{tag}</span>
      </div>
      <div className="tray-project-card__status">
        <span className="tray-project-card__dot" style={{ background: envColor }} />
        <span style={{ color: envColor }}>{env}</span>
      </div>
    </div>
  );
}

function OrgSwitcher() {
  const { user, activeOrg, setActiveOrg } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const orgs = (user?.orgs ?? []).filter(o => o !== 'lobby');
  const displayOrg = activeOrg || orgs[0] || 'lobby';

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="wm-taskbar__org-switcher"
        onClick={() => setOpen(!open)}
        title={`Organization: ${displayOrg}`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
        <span className="wm-taskbar__org-name">{displayOrg}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="wm-taskbar__org-dropdown">
          <div className="wm-taskbar__org-dropdown-header">Organizations</div>
          {orgs.length === 0 && (
            <div className="wm-taskbar__org-dropdown-empty">No organizations yet</div>
          )}
          {orgs.map(org => (
            <button
              key={org}
              className={`wm-taskbar__org-dropdown-item ${org === displayOrg ? 'active' : ''}`}
              onClick={() => { setActiveOrg(org); setOpen(false); }}
            >
              <span className="wm-taskbar__org-dropdown-dot" />
              {org}
            </button>
          ))}
          <div className="wm-taskbar__org-dropdown-divider" />
          <button
            className="wm-taskbar__org-dropdown-item"
            style={{ color: 'var(--brand-blue, #4f8fff)' }}
            onClick={() => { setOpen(false); }}
          >
            + Join organization
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [hasNotifications] = useState(false); // placeholder
  const [count] = useState(0);

  return (
    <button className="wm-taskbar__tray-bell" title={`${count} notifications`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 01-3.46 0" />
      </svg>
      {hasNotifications && (
        <span className="wm-taskbar__tray-bell-dot" />
      )}
    </button>
  );
}

/** Wrapper for a pinned app icon with hover preview */
function PinnedAppButton({
  app,
  isOpen,
  windowState,
  onClick,
  onContextMenu,
  onPointerDownCapture,
  onPointerEnterDrag,
  isDragTarget,
}: {
  app: { id: string; label: string; icon: string };
  isOpen: boolean;
  windowState?: { status: string; title: string };
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onPointerDownCapture: (e: React.PointerEvent) => void;
  onPointerEnterDrag: () => void;
  isDragTarget: boolean;
}) {
  const { visible, onMouseEnter, onMouseLeave } = usePreviewHover(300);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      {isDragTarget && <div className="wm-taskbar__drop-indicator" />}
      <button
        ref={btnRef}
        className={`wm-taskbar__app ${isOpen ? "wm-taskbar__app--active" : ""}`}
        onClick={onClick}
        onContextMenu={onContextMenu}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDownCapture}
        onPointerEnter={onPointerEnterDrag}
      >
        <svg viewBox="0 0 24 24">
          <path d={app.icon} />
        </svg>
      </button>
      {visible && (
        <TaskbarPreview
          label={app.label}
          windowState={windowState as any}
          anchorRef={btnRef}
        />
      )}
    </>
  );
}

/** Wrapper for a window strip button with hover preview */
function StripButton({
  win,
  isActive,
  onClick,
}: {
  win: { id: string; title: string; status: string };
  isActive: boolean;
  onClick: () => void;
}) {
  const { visible, onMouseEnter, onMouseLeave } = usePreviewHover(300);
  const btnRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={btnRef}
        className={`wm-taskbar__strip-btn ${isActive ? "wm-taskbar__strip-btn--active" : ""}`}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {win.title}
      </button>
      {visible && (
        <TaskbarPreview
          label={win.title}
          windowState={win as any}
          anchorRef={btnRef}
        />
      )}
    </>
  );
}

export function Taskbar({ provider, onOpenWindow, activeWindowId }: TaskbarProps) {
  const { windows, focusWindow, restoreWindow, closeWindow } = useWindowManager();
  const [startOpen, setStartOpen] = useState(false);
  const [personalizationOpen, setPersonalizationOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const taskbarCtx = useContextMenu();
  const [pinnedSet, setPinnedSet] = useState<Set<string>>(() => new Set(PINNED_APPS.map((a) => a.id)));

  // Drag reorder state
  const [pinnedOrder, setPinnedOrder] = useState<string[]>(() => {
    const saved = loadPinnedOrder();
    if (saved) {
      const pinnedIds = PINNED_APPS.map((a) => a.id);
      const valid = saved.filter((id) => pinnedIds.includes(id));
      const missing = pinnedIds.filter((id) => !valid.includes(id));
      return [...valid, ...missing];
    }
    return PINNED_APPS.map((a) => a.id);
  });
  const [dragSourceIdx, setDragSourceIdx] = useState<number | null>(null);
  const [dragTargetIdx, setDragTargetIdx] = useState<number | null>(null);
  const isDragging = useRef(false);
  const dragTargetIdxRef = useRef<number | null>(null);

  // Listen for keyboard-shortcut-driven toggles from Desktop
  useEffect(() => {
    const toggleStart = () => setStartOpen((v) => !v);
    const togglePersonalization = () => setPersonalizationOpen((v) => !v);
    window.addEventListener("opentool-toggle-start-menu", toggleStart);
    window.addEventListener("opentool-toggle-personalization", togglePersonalization);
    return () => {
      window.removeEventListener("opentool-toggle-start-menu", toggleStart);
      window.removeEventListener("opentool-toggle-personalization", togglePersonalization);
    };
  }, []);

  const orderedPinned = pinnedOrder
    .map((id) => PINNED_APPS.find((a) => a.id === id))
    .filter(Boolean) as typeof PINNED_APPS[number][];

  const handleAppClick = (id: string) => {
    if (isDragging.current) return;
    const win = windows.find((w) => w.id === id);
    if (win) {
      if (win.status === "minimized") {
        restoreWindow(id);
      } else {
        focusWindow(id);
      }
    } else {
      onOpenWindow(id);
    }
    window.location.hash = id;
  };

  const handleStripClick = (id: string) => {
    const win = windows.find((w) => w.id === id);
    if (!win) return;
    if (win.status === "minimized") {
      restoreWindow(id);
    } else {
      focusWindow(id);
    }
    window.location.hash = id;
  };

  // Drag reorder via pointer events
  const handleDragStart = useCallback((idx: number, e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;

    const onMove = (ev: PointerEvent) => {
      const dx = Math.abs(ev.clientX - startX);
      const dy = Math.abs(ev.clientY - startY);
      if (dx > 5 || dy > 5) {
        moved = true;
        isDragging.current = true;
        setDragSourceIdx(idx);
      }
    };

    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);

      if (moved) {
        const targetIdx = dragTargetIdxRef.current;
        if (targetIdx !== null && targetIdx !== idx) {
          setPinnedOrder((prev) => {
            const updated = [...prev];
            const [item] = updated.splice(idx, 1);
            updated.splice(targetIdx > idx ? targetIdx - 1 : targetIdx, 0, item);
            savePinnedOrder(updated);
            return updated;
          });
        }
        dragTargetIdxRef.current = null;
        setDragTargetIdx(null);
        setDragSourceIdx(null);
        setTimeout(() => {
          isDragging.current = false;
        }, 50);
      } else {
        setDragSourceIdx(null);
        setDragTargetIdx(null);
      }
    };

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }, []);

  const handleDragOver = useCallback((idx: number) => {
    if (dragSourceIdx !== null && dragSourceIdx !== idx) {
      dragTargetIdxRef.current = idx;
      setDragTargetIdx(idx);
    }
  }, [dragSourceIdx]);

  const handleAppContextMenu = useCallback(
    (appId: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const win = windows.find((w) => w.id === appId);
      const isOpen = !!win;
      const isPinned = pinnedSet.has(appId);

      const items: ContextMenuItem[] = [
        {
          label: isOpen ? "Focus" : "Open",
          icon: isOpen ? "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" : "M5 3l14 9-14 9V3z",
          action: () => {
            if (isOpen) {
              if (win!.status === "minimized") restoreWindow(appId);
              else focusWindow(appId);
            } else {
              onOpenWindow(appId);
            }
            window.location.hash = appId;
          },
        },
        {
          label: "Open New Window",
          icon: "M12 5v14 M5 12h14",
          action: () => {
            onOpenWindow(appId);
            window.location.hash = appId;
          },
        },
      ];

      if (isOpen) {
        items.push({
          label: "Close",
          icon: "M18 6L6 18 M6 6l12 12",
          action: () => closeWindow(appId),
        });
      }

      items.push("separator");

      items.push({
        label: isPinned ? "Unpin from Taskbar" : "Pin to Taskbar",
        icon: isPinned
          ? "M3 3l18 18 M10 2h4v7a1 1 0 001 1h3l-2 5"
          : "M12 17v5 M5 10h14 M6 3h12v7a1 1 0 01-1 1H7a1 1 0 01-1-1V3z",
        action: () => {
          if (isPinned) {
            setPinnedSet((prev) => {
              const next = new Set(prev);
              next.delete(appId);
              return next;
            });
          } else {
            setPinnedSet((prev) => new Set(prev).add(appId));
          }
        },
      });

      taskbarCtx.show(e, items);
    },
    [windows, pinnedSet, focusWindow, restoreWindow, closeWindow, onOpenWindow, taskbarCtx],
  );

  const openWindows = windows.filter(
    (w) => !PINNED_APPS.some((p) => p.id === w.id),
  );

  return (
    <>
      {taskbarCtx.render()}

      {startOpen && (
        <StartMenu
          onNavigate={(id) => {
            onOpenWindow(id);
            window.location.hash = id;
            setStartOpen(false);
          }}
          onClose={() => setStartOpen(false)}
          activeWindowId={activeWindowId}
        />
      )}

      {personalizationOpen && (
        <PersonalizationMenu onClose={() => setPersonalizationOpen(false)} />
      )}

      {calendarOpen && (
        <MiniCalendar onClose={() => setCalendarOpen(false)} />
      )}

      <div className="wm-taskbar" onContextMenu={(e) => e.preventDefault()}>
        {/* Start button */}
        <button
          className={`wm-taskbar__start ${startOpen ? "wm-taskbar__start--active" : ""}`}
          onClick={() => setStartOpen((v) => !v)}
        >
          <span className="wm-taskbar__start-prompt" style={{ color: '#4f8fff', fontWeight: 700 }}>&gt;</span><span style={{ color: '#e6edf3', fontWeight: 700 }}>tool</span><span style={{ color: '#4f8fff', fontWeight: 700 }}>_os</span>
        </button>

        <div className="wm-taskbar__divider" />

        {/* Pinned apps (drag-reorderable) */}
        <div className="wm-taskbar__apps">
          {orderedPinned.map((app, idx) => {
            const win = windows.find((w) => w.id === app.id);
            const isOpen = !!win;
            return (
              <PinnedAppButton
                key={app.id}
                app={app}
                isOpen={isOpen}
                windowState={win}
                onClick={() => handleAppClick(app.id)}
                onContextMenu={(e) => handleAppContextMenu(app.id, e)}
                onPointerDownCapture={(e) => handleDragStart(idx, e)}
                onPointerEnterDrag={() => handleDragOver(idx)}
                isDragTarget={dragTargetIdx === idx && dragSourceIdx !== null}
              />
            );
          })}
        </div>

        <div className="wm-taskbar__divider" />

        {/* Window strip -- non-pinned open windows */}
        <div className="wm-taskbar__strip">
          {openWindows.map((win) => (
            <StripButton
              key={win.id}
              win={win}
              isActive={win.id === activeWindowId}
              onClick={() => handleStripClick(win.id)}
            />
          ))}
        </div>

        <div className="wm-taskbar__spacer" />

        {/* System tray */}
        <div className="wm-taskbar__tray">
          {/* Org switcher */}
          <OrgSwitcher />

          <div className="wm-taskbar__tray-divider" />

          {/* Health indicator with hover card */}
          <TrayProjectWidget />

          <div className="wm-taskbar__tray-divider" />

          {/* Provider */}
          <div className="wm-taskbar__tray-provider">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
              <path d="M12 2a4 4 0 014 4v2H8V6a4 4 0 014-4z" />
              <rect x="3" y="8" width="18" height="12" rx="2" />
              <circle cx="12" cy="15" r="2" />
            </svg>
            <span className="wm-taskbar__tray-dot" />
            {provider === "none" ? "no provider" : "model connected"}
          </div>

          <div className="wm-taskbar__tray-divider" />

          {/* Personalization button */}
          <button
            className={`wm-taskbar__tray-palette ${personalizationOpen ? "wm-taskbar__tray-palette--active" : ""}`}
            title="Personalize"
            onClick={() => setPersonalizationOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="13.5" cy="6.5" r="0.5" fill="currentColor" stroke="none" />
              <circle cx="17.5" cy="10.5" r="0.5" fill="currentColor" stroke="none" />
              <circle cx="8.5" cy="7.5" r="0.5" fill="currentColor" stroke="none" />
              <circle cx="6.5" cy="12.5" r="0.5" fill="currentColor" stroke="none" />
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 011.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
            </svg>
          </button>

          {/* Keyboard shortcuts */}
          <button
            className="wm-taskbar__tray-bell"
            title="Keyboard Shortcuts (Ctrl+/)"
            onClick={() => window.dispatchEvent(new CustomEvent("opentool-toggle-shortcuts"))}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="6" width="20" height="12" rx="2" />
              <path d="M6 10h0 M10 10h0 M14 10h0 M18 10h0 M8 14h8" />
            </svg>
          </button>

          {/* Notification bell */}
          <NotificationBell />

          {/* Clock + date (click for calendar) */}
          <Clock onClick={() => setCalendarOpen((v) => !v)} />
        </div>
      </div>
    </>
  );
}
